// One inline text editor's life cycle (docs/claude/ui.md, *One inline text
// editor*): it opens over what it edits, rides one undo gesture, keeps its look
// whole, puts the caret where its target asks once the opening click is over,
// and closes the same way whatever closes it — Enter / Escape by its target's
// rules (inlineText.ts), a click away (blur commits), or the app. A target says
// only how it differs: its text, its look, its keys, and what typing, committing
// and closing do to it.
import {
  type EditorLook,
  editorStyle,
  inlineKeyAction,
  type KeyRules,
} from '../inlineText';
import {
  beginGesture,
  commitGesture,
} from './core';

/** What an inline editor edits. */
export interface InlineTarget {
  /** The text it opens with. */
  text: string;
  /** Where it sits and how it looks, now (re-read by `place`). */
  look(): EditorLook;
  keys: KeyRules;
  /** Its edit is one undo step of its own (a re-edit; a new object's text rides
   *  its creation's step). */
  gesture: boolean;
  /** Where the caret goes on opening: after the text, or the text selected. */
  caret: 'all' | 'end';
  /** Each keystroke's text, live. */
  input?(text: string): void;
  /** The caret moved (a target that paints its own caret repaints). */
  caretMoved?(): void;
  /** Keep what was typed. */
  commit(text: string): void;
  /** It closed, committed or not: the target lets go of what it held. */
  closed(): void;
}

export interface InlineEditor {
  /** Open over a target — closing (committing) whatever it was editing first. */
  open(t: InlineTarget): void;
  /** Close, keeping what was typed or not. */
  close(commit: boolean): void;
  /** The target being edited, or null. */
  current(): InlineTarget | null;
  /** Re-apply the target's look (each frame: its box may have moved). */
  place(): void;
  /** The first keystroke that opened it (typing over a new box): focused NOW —
   *  no click is coming, and the next keys would reach the drawing first. */
  type(text: string): void;
}

/** The life cycle over one element. `own`: the element is the editor's for this
 *  edit alone (a title-block field's), removed when it closes. */
export function inlineEditor(el: HTMLTextAreaElement | HTMLInputElement, own = false): InlineEditor {
  let target: InlineTarget | null = null;
  let gesturing = false;
  const place = (): void => { if (target) Object.assign(el.style, editorStyle(target.look())); };
  const close = (commit: boolean): void => {
    const t = target;
    if (!t) return;
    target = null; // first: a blur fired by what follows finds nothing open
    if (commit) t.commit(el.value);
    if (gesturing) { commitGesture(); gesturing = false; }
    el.classList.add('hidden');
    Object.assign(el.style, editorStyle({})); // closed, it holds no target's look
    t.closed();
    if (own) el.remove();
  };
  el.addEventListener('input', () => target?.input?.(el.value));
  el.addEventListener('keydown', (e) => {
    const ev = e as KeyboardEvent;
    ev.stopPropagation(); // no global shortcut while typing
    if (!target) return;
    const act = inlineKeyAction(ev.key, { shift: ev.shiftKey, ctrl: ev.ctrlKey || ev.metaKey }, target.keys);
    if (!act) return;
    ev.preventDefault();
    close(act === 'commit');
  });
  for (const ev of ['keyup', 'pointerup', 'select'] as const) el.addEventListener(ev, () => target?.caretMoved?.());
  el.addEventListener('blur', () => close(true));
  return {
    open(t) {
      close(true);
      target = t;
      if (t.gesture) { beginGesture(); gesturing = true; }
      el.value = t.text;
      place();
      el.classList.remove('hidden');
      // After the opening click: the canvas taking focus on mouseup would blur the
      // editor shut, and typing would be lost.
      setTimeout(() => {
        if (target !== t) return;
        el.focus();
        if (t.caret === 'all') el.select();
        else el.setSelectionRange(el.value.length, el.value.length);
      }, 0);
    },
    close,
    current: () => target,
    place,
    type(text) {
      if (!target) return;
      el.focus();
      el.value = text;
      target.input?.(text);
      el.setSelectionRange(text.length, text.length);
    },
  };
}
